<script setup>
import axios from 'axios';
import { ref, watch } from 'vue';
import PgeButton from '../../../../pge-components/pge-button.vue';
import PgeInputText from '../../../../pge-components/pge-input-text.vue';

const props = defineProps({
	eventData: Object,
	changeFormStep: Function,
	currentFormStepData: {
		type: Object,
		default: () => ({}),
	},
});

const emailInput = ref(props.currentFormStepData.email || '');
const isLoading = ref(false);
const hasError = ref(false);
const errors = ref([]);
const sent = ref(false);

watch(emailInput, newVal => {
	hasError.value = !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(newVal);
	errors.value = [];
});

async function handleSubmit() {
	if (!emailInput.value || hasError.value || isLoading.value) return;

	isLoading.value = true;
	errors.value = [];

	try {
		await axios.post('/api/sso/api/forgot-password', {
			email: emailInput.value,
			// Keep recovery on this Hub: the reset link emails back to our own page.
			redirect_url: `${window.location.origin}/reset-password`,
		});
		sent.value = true;
	} catch (error) {
		errors.value = [error.response?.data?.message || 'Something went wrong. Please try again.'];
	} finally {
		isLoading.value = false;
	}
}
</script>

<template>
	<div class="forgot">
		<div class="forgot__title">Reset your password</div>

		<template v-if="sent">
			<div class="forgot__sent">
				If an account exists for <strong>{{ emailInput }}</strong>, we've sent a link to reset your password.
				Check your inbox and follow the link to continue here.
			</div>
			<div class="forgot__actions forgot__actions--center">
				<pge-button label="Back to login" @click="() => props.changeFormStep('check-email')" />
			</div>
		</template>

		<template v-else>
			<div class="forgot__description">
				Enter your email address and we'll send you a link to reset your password.
			</div>
			<div class="bg-white">
				<pge-input-text type="email" name="email" label="Email Address" :model="emailInput" v-model="emailInput"
					:submit-on-enter="handleSubmit" required has-required-sign should-show-error no-tabindex />
			</div>
			<div class="forgot__actions">
				<pge-button label="Back" template="text" @click="() => props.changeFormStep('check-email')" />
				<pge-button :label="isLoading ? 'Sending...' : 'Send reset link'" @click="handleSubmit"
					:disabled="!emailInput || hasError || isLoading" />
			</div>
			<div class="forgot__errors" v-if="errors.length">
				<div v-for="error in errors" :key="error">{{ error }}</div>
			</div>
		</template>
	</div>
</template>

<style lang="scss" scoped>
.forgot {
	display: flex;
	flex-direction: column;
	gap: 18px;

	&__title {
		text-align: center;
		font-weight: 700;
		font-size: 18px;
		line-height: 1.5;
		color: #000;
	}

	&__description,
	&__sent {
		font-size: 14px;
		color: #49454f;
		text-align: center;
		line-height: 1.5;
	}

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;

		&--center {
			justify-content: center;
		}
	}

	&__errors {
		display: flex;
		flex-direction: column;
		gap: 10px;

		div {
			text-align: center;
			color: #f83c3c;
			font-weight: 700;
			font-size: 14px;
			line-height: 20px;
		}
	}
}

.bg-white {
	background: white;
}
</style>
